/* eslint-disable react/jsx-no-useless-fragment */

import { parseDateValue } from '@ben/common/date';
import { sanitize } from '@ben/common/dom';
import DateInput from '@ben/orion/date-input';
import Select from '@ben/orion/deprecated-select';
import Input, { InputVariant } from '@ben/orion/input';
import InputFeedback from '@ben/orion/input-feedback';
import InputLabel from '@ben/orion/input-label/input-label';
import PensionSliders from '@ben/orion/pension-sliders';
import Slider from '@ben/orion/slider';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { CustomComponentTypes } from '@ben/shared/constants';
import { SliderValueType } from '../../../types/columbia';
import type { IValidationError } from '@ben/shared/types/validation';
import RequiredCheckboxWrapper from '@ben/shared/components/benefit-sections/required-checkbox-wrapper';
import { useFormulaMinMaxField } from '@ben/shared/hooks/use-formula-min-max-field';
import { normalizeNonBreakingSpaces } from '@ben/shared/utils/normalize-non-breaking-spaces';
import { format, sub } from 'date-fns';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, useTheme } from 'styled-components';
import Beneficiaries from '../beneficiaries';
import BuyAndSellHoliday from '../buy-and-sell-holiday';
import CalculatedValueComponent from '../calculated-value';
import ConditionalApprovalInfo from '../conditional-approval-info';
import Contacts from '../contacts/contacts';
import DocumentSigning from '../document-signing';
import FileUploadControl from '../file-upload-control';
import ModularBenefitComponentWrapper from '../modular-benefit-component-wrapper';
import { MultiSelectOptions } from '../multi-select-options';
import MultiSelectWithSubform from '../multi-select-with-subform';
import WindowInfo from '../window-info';
import { getMaxDate } from './custom-component-utils';

const today = new Date();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const CustomComponent = (props: any) => {
  // Omit props that we don’t want to end up in the DOM
  const {
    type,
    selectValue,
    haserror,
    contactType,
    configWindow,
    isConditionalApprovalTriggered,
    ...rest
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const handleValidationErrorCallback = useCallback(
    (error: IValidationError) => {
      if (rest.setValidation) {
        rest.setValidation(error.hasError, error.message);
      }
    },
    [rest]
  );

  const {
    min: effectiveMin,
    max: effectiveMax,
    sellMax: recalculatedSellMax,
    isLoading: isFormulaMinMaxLoading,
  } = useFormulaMinMaxField({
    ...rest,
    clampValueOnBoundsChange:
      type !== CustomComponentTypes.BUY_SELL_HOLIDAY &&
      type !== CustomComponentTypes.AMOUNT_INPUT,
  });

  let Component = <></>;
  switch (type) {
    case CustomComponentTypes.TEXT_INPUT: {
      Component = (
        <Input
          {...rest}
          variant={
            rest.variant === 'TEXT_AREA'
              ? InputVariant.TextArea
              : InputVariant.Text
          }
          defaultValue={rest.value || rest.default}
          showLabel={rest.showName}
          width="100%"
          hasError={haserror || rest.hasError}
        />
      );
      break;
    }
    case CustomComponentTypes.AMOUNT_INPUT: {
      const { interval, ...restProps } = rest;
      const intervalValue = Number(interval);
      const example = [
        1 * intervalValue,
        2 * intervalValue,
        3 * intervalValue,
      ].join(', ');

      Component = (
        <Input
          {...restProps}
          showLabel={rest.showName}
          variant={InputVariant.Amount}
          defaultValue={rest.value ?? rest.default}
          width="100%"
          validationErrorCallback={handleValidationErrorCallback}
          hasError={haserror || rest.hasError}
          min={effectiveMin}
          max={effectiveMax}
          disabled={rest.disabled}
          hintText={
            interval
              ? t('benefit.amountInput.intervalHint', {
                  interval: intervalValue,
                  example,
                })
              : undefined
          }
        />
      );
      break;
    }
    case CustomComponentTypes.TEXT_BLOCK: {
      const normalizedDescription = normalizeNonBreakingSpaces(
        rest.description
      );

      Component = (
        <TextBlockContainer>
          {rest.showName && (
            <InputLabel
              $hasError={haserror || rest.hasError}
              htmlFor={rest.id}
              theme={theme}
            >
              {rest.name}
            </InputLabel>
          )}
          <TypographyContainer
            variant={TypographyVariant.bodyLg1}
            className="ql-editor"
            dangerouslySetInnerHTML={{
              __html: sanitize(normalizedDescription),
            }}
          />
        </TextBlockContainer>
      );
      break;
    }
    case CustomComponentTypes.DROPDOWN: {
      const selectValue = rest.value || rest.default || rest.defaultValue;
      Component = (
        <Select
          {...rest}
          defaultValue={selectValue}
          width="100%"
          hasError={haserror || rest.hasError}
          clearable={!rest.required}
        />
      );
      break;
    }
    case CustomComponentTypes.CHECKBOX: {
      Component = (
        <RequiredCheckboxWrapper
          {...rest}
          label={rest.name}
          defaultChecked={rest.default}
          hasError={haserror || rest.hasError}
          validationErrorCallback={handleValidationErrorCallback}
          requireTrue={rest.requireTrue}
        />
      );
      break;
    }
    case CustomComponentTypes.PENSION_SLIDERS: {
      const {
        employeeMax,
        employeeMin,
        employerMax,
        employerMin,
        increment,
        onChange,
        value,
        employeeDefault,
        name,
        showName,
        hintText,
        disabled,
        isOptIn,
        errorMessage,
      } = rest;

      Component = (
        <ModularBenefitComponentWrapper title={name} showTitle={showName}>
          <PensionSliders
            employeeProps={{
              minimumPercentage: Number(employeeMin),
              maximumPercentage: Number(employeeMax),
              label: t('programme.pension.youContribute'),
            }}
            employerProps={{
              minimumPercentage: Number(employerMin),
              maximumPercentage: Number(employerMax),
              label: t('programme.pension.employerContribute'),
            }}
            employeePercentage={
              Number(value?.employeePercentage) ?? Number(employeeDefault)
            }
            onChange={onChange}
            hintText={hintText}
            isDisabled={disabled}
            step={increment}
          />
          {errorMessage && <InputFeedback errorMessage={errorMessage} />}
          {configWindow && !isOptIn && (
            <WindowInfo
              startDate={configWindow.startTimestamp}
              endDate={configWindow.endTimestamp}
            />
          )}
          {isConditionalApprovalTriggered && <ConditionalApprovalInfo />}
        </ModularBenefitComponentWrapper>
      );
      break;
    }
    case CustomComponentTypes.FILE_UPLOAD: {
      const { showName, errorMessage } = rest;
      Component = (
        <>
          <FileUploadControl {...rest} label={rest.name} showLabel={showName} />
          {errorMessage && <InputFeedback errorMessage={errorMessage} />}
        </>
      );
      break;
    }
    case CustomComponentTypes.BUY_SELL_HOLIDAY: {
      const {
        default: defaultValue,
        onChange,
        value,
        name,
        showName,
        unitType,
        interval,
        isOptIn,
        errorMessage,
        hasError,
        sellMax,
      } = rest;
      const isParentErrorVisible = Boolean(errorMessage && hasError);

      // Sell capacity, freshest source first: live recalculation response,
      // then the sell_max serialized with the benefit payload, then (for
      // API responses predating sell_max) the negative part of the
      // effective min.
      const legacySellMax = effectiveMin < 0 ? Math.abs(effectiveMin) : 0;
      const sellCapacity = recalculatedSellMax ?? sellMax ?? legacySellMax;

      const additionalConfig = {
        buyMax: effectiveMax?.toFixed(2),
        sellMax: sellCapacity?.toFixed(2),
        unitType,
        interval,
      };

      const handleChange = (newValue: string) => {
        onChange(Number(newValue));
      };
      // eventually we want to make all numeric values numbers here but this component is used elsewhere with strings
      Component = (
        <ModularBenefitComponentWrapper
          title={name}
          showTitle={showName}
          wrapChildren={false}
        >
          <BuyAndSellHoliday
            {...rest}
            additionalConfig={additionalConfig}
            setNumberOfHolidays={handleChange}
            numberOfHolidays={value ? Number(value).toFixed(2) : '0.00'}
            hasMadeChanges={value !== defaultValue} //make this default value instead of 0.0
            hideValidationMessage={isParentErrorVisible}
            justifyContent="space-between"
          />
          {errorMessage && hasError && (
            <InputFeedback errorMessage={errorMessage} />
          )}
          {configWindow && !isOptIn && (
            <WindowInfo
              startDate={configWindow.startTimestamp}
              endDate={configWindow.endTimestamp}
            />
          )}
          {isConditionalApprovalTriggered && <ConditionalApprovalInfo />}
        </ModularBenefitComponentWrapper>
      );
      break;
    }
    case CustomComponentTypes.MULTI_SELECT: {
      const {
        name,
        showName,
        options,
        onChange,
        value,
        defaultValue,
        insideBox,
        maxSelections,
        minSelections,
        benefitId,
        isOptIn,
        errorMessage,
        backendErrors,
        disabled,
      } = rest;

      Component = (
        <>
          <MultiSelectOptions
            options={options}
            onChange={onChange}
            borderColor={
              insideBox ? theme.palette.primary.bg : theme.palette.neutral[60]
            }
            values={value ?? defaultValue}
            title={name}
            showTitle={showName}
            max={maxSelections ? Number(maxSelections) : undefined}
            min={minSelections ? Number(minSelections) : undefined}
            validationErrorCallback={handleValidationErrorCallback}
            benefitId={benefitId}
            configWindow={configWindow}
            isConditionalApprovalTriggered={isConditionalApprovalTriggered}
            isOptIn={isOptIn}
            backendErrors={backendErrors}
            disabled={disabled}
          />
          {errorMessage && <InputFeedback errorMessage={errorMessage} />}
        </>
      );
      break;
    }

    case CustomComponentTypes.MULTI_SELECT_WITH_SUBFORM: {
      const {
        name,
        showName,
        options,
        onChange,
        value,
        defaultValue,
        insideBox,
        maxSelections,
        minSelections,
        benefitId,
        errorMessage,
        disabled,
      } = rest;

      Component = (
        <>
          <MultiSelectWithSubform
            name={name}
            showName={showName}
            options={options}
            onChange={onChange}
            value={value ?? defaultValue ?? []}
            insideBox={insideBox}
            maxSelections={maxSelections}
            minSelections={minSelections}
            benefitId={benefitId}
            handleValidationErrorCallback={handleValidationErrorCallback}
            configWindow={configWindow}
            isConditionalApprovalTriggered={isConditionalApprovalTriggered}
            disabled={disabled}
          />
          {errorMessage && <InputFeedback errorMessage={errorMessage} />}
        </>
      );
      break;
    }
    case CustomComponentTypes.DATE_INPUT: {
      const { value, placeholder, name, showName, ...restProps } = rest;
      const maxDate = getMaxDate(contactType, rest?.allowFutureDates);
      Component = (
        <DateInput
          {...restProps}
          showLabel={showName}
          dateFormat={rest.dateFormat?.toUpperCase()}
          withinPortal={false}
          value={
            value
              ? parseDateValue(value)
              : rest.default
                ? parseDateValue(rest.default)
                : undefined
          }
          onChange={(date) =>
            rest.onChange(date ? format(date, 'yyyy-MM-dd') : '')
          }
          width="100%"
          label={name}
          isOptional={!rest.required}
          required={undefined} // otherwise a star is shown in label
          clearable={false}
          errorMessage={haserror || rest.hasError ? rest.errorMessage : ''}
          maxDate={maxDate}
          minDate={sub(today, { years: 120 })}
        />
      );
      break;
    }

    case CustomComponentTypes.CONTACTS: {
      const {
        errorMessage,
        componentKey,
        name,
        value,
        showName,
        originalUserSelection,
        backendErrors,
      } = rest;
      Component = (
        <>
          <Contacts
            {...rest}
            componentKey={componentKey}
            title={name}
            values={value}
            showTitle={showName}
            configWindow={configWindow}
            isConditionalApprovalTriggered={isConditionalApprovalTriggered}
            originalUserSelection={originalUserSelection}
            backendErrors={backendErrors}
          />
          {errorMessage && <InputFeedback errorMessage={errorMessage} />}
        </>
      );
      break;
    }
    case CustomComponentTypes.BENEFICIARIES: {
      const { errorMessage, name, value, showName, backendErrors } = rest;
      Component = (
        <>
          <Beneficiaries
            {...rest}
            title={name}
            values={value}
            showTitle={showName}
            configWindow={configWindow}
            isConditionalApprovalTriggered={isConditionalApprovalTriggered}
            handleValidationErrorCallback={handleValidationErrorCallback}
            backendErrors={backendErrors}
          />
          {errorMessage && <InputFeedback errorMessage={errorMessage} />}
        </>
      );
      break;
    }

    case CustomComponentTypes.SLIDER: {
      const {
        sliderValueType,
        default: defaultValue,
        value,
        increment = 1,
        showName,
        disabled,
      } = rest;

      const { hasError: restHasError, errorMessage } = rest;
      const clampedValue = Math.min(
        Math.max(Number(value), effectiveMin),
        effectiveMax
      );
      Component = (
        <>
          {/*
           * Keyed on bounds so rc-slider remounts when the formula-driven
           * min/max change. Without this, rc-slider's track fill gets stuck
           * at a stale pixel position because (value - min) / (max - min)
           * briefly produces NaN (e.g. when min === max after clamping), and
           * browsers silently ignore invalid CSS values rather than resetting
           * them, leaving the track frozen at the previous position.
           * A fresh mount has no stale CSS to inherit, so NaN% falls back to 0%.
           */}
          <Slider
            {...rest}
            key={`${effectiveMin}-${effectiveMax}`}
            isPercentage={sliderValueType === SliderValueType.PERCENTAGE}
            min={effectiveMin}
            max={effectiveMax}
            defaultValue={Number(defaultValue)}
            value={clampedValue}
            step={Number(increment)}
            showLabel={showName}
            disabled={disabled || isFormulaMinMaxLoading}
            hasError={haserror || restHasError}
          />
          {errorMessage && <InputFeedback errorMessage={errorMessage} />}
        </>
      );
      break;
    }

    case CustomComponentTypes.CALCULATED_VALUE: {
      Component = (
        <CalculatedValueComponent
          {...rest}
          id={rest.id}
          setBenefitForm={rest.setBenefitForm}
          benefitId={rest.benefitId}
          enrollmentId={rest.enrollmentId}
          title={rest.showName ? rest.name : undefined}
          description={rest.valueDescription}
          values={[
            {
              label: rest.label,
              value: rest.value,
              outputValueType: rest.outputValueType,
              outputValueSymbol: rest.outputValueSymbol,
            },
          ]}
        />
      );
      break;
    }
    case CustomComponentTypes.DOCUMENT_SIGNING: {
      Component = (
        <DocumentSigning
          id={rest.id}
          name={rest.name}
          description={rest.description}
          disabled={rest.disabled}
          benefitId={rest.benefitId}
          benefitForm={rest.benefitForm}
          value={rest.value}
          allSigningRequests={rest.allSigningRequests ?? []}
          needsResigning={rest.needsResigning}
          hasChanges={rest.hasChanges}
          initialSigningValue={rest.initialSigningValue}
          onChange={rest.onChange}
        />
      );
      break;
    }
  }

  return Component;
};

export default memo(CustomComponent);

const TypographyContainer = styled(Typography)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 100%;
  overflow-wrap: break-word;
  word-break: normal;
  text-align: left;
  padding: 0;

  p:empty::before {
    content: '\\00a0';
  }

  & > ul {
    margin-left: 1rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  img {
    max-width: 100%;
  }
`;

const TextBlockContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    max-width: 100%;
    gap: ${theme.spacing(0.5)};
  `}
`;
